import { Component, Input } from "@angular/core";
import { ApiService } from "src/app/config/api.service";
import {
	Merchant,
	Offer,
	OffersApiResponse,
	Tag,
} from "../../interfaces/offers.interface";
import { finalize } from "rxjs";
import { CommonService } from "../../services/common/common.service";
import { AuthService } from "../../services/auth/auth.service";
import { Router } from "@angular/router";
import { Carousel } from "primeng/carousel";

interface TabMenuItem {
	label: string;
	icon: string;
	code: string;
}

@Component({
	selector: "app-tag-based-offers",
	templateUrl: "./tag-based-offers.component.html",
	styleUrls: ["./tag-based-offers.component.scss"],
})
export class TagBasedOffersComponent {
	@Input() categoryCode?: string;
	@Input() categoryRefresh?: boolean;
	@Input() categoryName?: string;
	public offers: Offer[] = [];
	public isLoadingOffers: boolean = true;
	public isLoadingTags: boolean = true;
	public offersByTag: Record<string, Offer[]> = {};
	public activeTag: TabMenuItem;
	public tabMenuItems: TabMenuItem[];
	public merchants: Merchant[] = [];
	public isMobile: boolean = this.commonService.isDeviceMobile();
	public tags: Tag[] = [];
	private homepageTags: Tag[] = [
		{
			name: $localize`Popular Offers`,
			code: "popular-offers",
		},
		{
			name: $localize`Limited-time Offers`,
			code: "limited-time-offers",
		},
		{
			name: $localize`Local Offers`,
			code: "local-offers",
		},
		{
			name: $localize`Startup Pack`,
			code: "startup-pack",
		},
		{
			name: $localize`Travel Pack`,
			code: "travel-pack",
		},
		{
			name: $localize`Digital Advertising`,
			code: "digital-advertising",
		},
		{
			name: $localize`E-Commerce`,
			code: "ecommerce",
		},
	];
	private isListingPage: boolean = false;

	constructor(
		private apiService: ApiService,
		private commonService: CommonService,
		public _authService: AuthService,
		private router: Router
	) {
		Carousel.prototype.onTouchMove = () => { };
		this.isListingPage = this.router.url.includes('/list')
		if(!this.isListingPage) this.getTagBasedOffers();
	}

	public onTagSelection(activeTag): void {
		if(!this.isLoadingTags)
		this.activeTag = activeTag;	
		this.offers = [];
		this.getOffers();
	}

	private getTagBasedOffers(): void {
			this.getTags();
			this.tags = this.homepageTags;
			this.setTabMenuItems();
			this.isLoadingTags = false;
			this.getOffers();
	}

	public ngOnChanges(): void {
		if(this.categoryRefresh) {
			this.isLoadingTags = true;
			this.isLoadingOffers = true;
			this.tags = [];
			this.offers = [];
			this.offersByTag = {};
			this.tabMenuItems = [];
			this.merchants = [];
			this.getTagBasedOffers();
		} else if(this.categoryCode) {
			this.getTagBasedOffers();
		}
		
	}

	public convertToSentenceCase(str: string): string {
		if (!str) return '';
        const words = str.split(' ');
        const firstWord = words[0];
        const restOfSentence = words.slice(1).join(' ');
        return firstWord + ' ' + restOfSentence.toLowerCase();
	}

	private setTabMenuItems(): void {
		this.tabMenuItems = this.tags.map((tag) => {
			return { label: this.convertToSentenceCase(tag.name), icon: "", code: tag.code };
		});
		this.activeTag = this.tabMenuItems?.[0];
	}

	private getTags(): void {
		this.isLoadingTags = true;
		const params = {};
		if (this.categoryCode) params["categories"] = [this.categoryCode];
		this.apiService.getOfferTags(params)
			.pipe(finalize(() => (this.isLoadingTags = false)))
			.subscribe({
				next: (response: Tag[]) => {
					this.tags = response ?? [];
					if(this.isListingPage) {
						const tagsToRemoveInListingPage = [];
						this.homepageTags.forEach((tag: Tag) => tagsToRemoveInListingPage.push(tag.code));
						this.tags = this.tags.filter((tag: Tag) => !tagsToRemoveInListingPage.includes(tag.code));
					} else {
						this.tags = this.homepageTags.filter((tag: Tag) => (this.tags || []).find((t: Tag) => t.code === tag.code));						
					}
					this.setTabMenuItems();
					this.getOffers();
				},
				error: () => {
					// Error handling done in services.
				  }
			},
		);
	}

	private getOffers(): void {
		this.isLoadingOffers = true;
		const params = {
			per_page: 50
		};
		if (this.categoryCode) params["categories"] = [this.categoryCode];
		if(this.activeTag) params["tags"] = this.activeTag.code;
		this.apiService
			.getOffers(params)
			.pipe(finalize(() => (this.isLoadingOffers = false)))
			.subscribe({
				next: (response: OffersApiResponse) => {
					this.merchants = response?.included?.filter(
						(info) => info.type === "merchant"
					);
					this.offers = response?.data?.map((offer: Offer) => {
						return {
							...offer,
							merchant: this.getMerchantDetails(offer),
							isDisabled: this._authService.isAuthenticated()
								? !offer.attributes.claimable
								: false,
						};
					});					
				}
			});
	}

	private getMerchantDetails(offer): Merchant {
		return this.merchants.find(
			(merchant) => merchant.id === offer.relationships.merchant.data.id
		);
	}
}
